import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ResolutionTracker = ({ charge, onBack }) => {
  const [activeTab, setActiveTab] = useState('status');
  const [newMessage, setNewMessage] = useState('');

  // Mock resolution stages (in production, this would come from backend)
  const resolutionStages = [
    {
      id: 'submitted',
      label: 'Submitted',
      icon: 'FileText',
      status: 'completed',
      completedDate: charge?.disputeDate,
      estimatedDays: 0,
      description: 'Your dispute has been submitted and logged in our system.',
    },
    {
      id: 'under_review',
      label: 'Under Review',
      icon: 'Search',
      status: 'in_progress',
      estimatedDays: '2-3',
      description: 'Our team is reviewing your dispute claim and evidence.',
    },
    {
      id: 'evidence_evaluation',
      label: 'Evidence Evaluation',
      icon: 'Eye',
      status: 'pending',
      estimatedDays: '3-5',
      description: 'Submitted evidence is being evaluated by our dispute resolution team.',
    },
    {
      id: 'mediation',
      label: 'Mediation',
      icon: 'Users',
      status: 'pending',
      estimatedDays: '5-7',
      description: 'Mediator facilitates communication between parties to reach resolution.',
    },
    {
      id: 'resolved',
      label: 'Resolved',
      icon: 'CheckCircle',
      status: 'pending',
      estimatedDays: '7-10',
      description: 'Final decision has been made and communicated to all parties.',
    },
  ];

  // Mock communication thread
  const communications = [
    {
      id: 1,
      sender: 'You',
      role: 'renter',
      message: charge?.disputeReason || 'Dispute submitted with evidence.',
      timestamp: charge?.disputeDate,
      isSystem: false,
    },
    {
      id: 2,
      sender: 'System',
      role: 'system',
      message: 'Your dispute has been received and assigned to a mediator. You will receive updates as your case progresses.',
      timestamp: charge?.disputeDate,
      isSystem: true,
    },
  ];

  const getStageStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return { color: 'bg-success text-success-foreground', icon: 'CheckCircle' };
      case 'in_progress':
        return { color: 'bg-primary text-primary-foreground', icon: 'Loader' };
      case 'pending':
        return { color: 'bg-muted text-muted-foreground', icon: 'Circle' };
      default:
        return { color: 'bg-muted text-muted-foreground', icon: 'Circle' };
    }
  };

  const handleSendMessage = () => {
    if (!newMessage?.trim()) return;
    // In production, this would send message to backend
    console.log('Sending message:', newMessage);
    setNewMessage('');
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-smooth mb-6"
      >
        <Icon name="ArrowLeft" size={20} strokeWidth={2} />
        <span className="font-body text-sm">Back to Charge Selection</span>
      </button>

      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
              Dispute Resolution Tracking
            </h2>
            <p className="font-body text-sm text-muted-foreground">
              Case: {charge?.chargeReference}
            </p>
          </div>
          <div className="flex items-center space-x-2 px-4 py-2 bg-primary/10 text-primary border border-primary/20 rounded-full">
            <Icon name="AlertCircle" size={16} strokeWidth={2} />
            <span className="font-caption text-sm font-semibold">Under Dispute</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="font-caption text-xs text-muted-foreground mb-1">Disputed Amount</p>
            <p className="font-body text-lg font-bold text-foreground">${charge?.chargeAmount?.toFixed(2)}</p>
          </div>
          <div>
            <p className="font-caption text-xs text-muted-foreground mb-1">Dispute Filed</p>
            <p className="font-body text-sm text-foreground">
              {charge?.disputeDate ? new Date(charge?.disputeDate)?.toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <div>
            <p className="font-caption text-xs text-muted-foreground mb-1">Estimated Resolution</p>
            <p className="font-body text-sm text-foreground">7-10 business days</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab('status')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-body font-medium transition-smooth whitespace-nowrap ${
            activeTab === 'status' ?'bg-primary text-primary-foreground' :'bg-card text-foreground hover:bg-muted border border-border'
          }`}
        >
          <Icon name="TrendingUp" size={18} strokeWidth={2} />
          <span>Status Timeline</span>
        </button>
        <button
          onClick={() => setActiveTab('communication')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-body font-medium transition-smooth whitespace-nowrap ${
            activeTab === 'communication' ?'bg-primary text-primary-foreground' :'bg-card text-foreground hover:bg-muted border border-border'
          }`}
        >
          <Icon name="MessageSquare" size={18} strokeWidth={2} />
          <span>Communication</span>
        </button>
        <button
          onClick={() => setActiveTab('details')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-body font-medium transition-smooth whitespace-nowrap ${
            activeTab === 'details' ?'bg-primary text-primary-foreground' :'bg-card text-foreground hover:bg-muted border border-border'
          }`}
        >
          <Icon name="FileText" size={18} strokeWidth={2} />
          <span>Dispute Details</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="bg-card border border-border rounded-lg p-6">
        {/* Status Timeline Tab */}
        {activeTab === 'status' && (
          <div className="space-y-6">
            <h3 className="font-heading text-xl font-bold text-foreground mb-4">
              Resolution Progress
            </h3>
            <div className="relative">
              {resolutionStages?.map((stage, index) => {
                const config = getStageStatusConfig(stage?.status);
                const isLast = index === resolutionStages?.length - 1;

                return (
                  <div key={stage?.id} className="relative pb-8">
                    {!isLast && (
                      <div
                        className={`absolute left-6 top-12 w-0.5 h-full ${
                          stage?.status === 'completed' ? 'bg-success' : 'bg-border'
                        }`}
                      />
                    )}
                    <div className="flex items-start space-x-4">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${config?.color}`}
                      >
                        <Icon
                          name={stage?.icon}
                          size={24}
                          strokeWidth={2}
                          className={stage?.status === 'in_progress' ? 'animate-pulse' : ''}
                        />
                      </div>
                      <div className="flex-1 pt-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-body font-semibold text-foreground">{stage?.label}</h4>
                          {stage?.status === 'completed' && stage?.completedDate && (
                            <span className="font-caption text-xs text-muted-foreground">
                              {new Date(stage?.completedDate)?.toLocaleDateString()}
                            </span>
                          )}
                          {stage?.status === 'pending' && (
                            <span className="font-caption text-xs text-muted-foreground">
                              Est. {stage?.estimatedDays} days
                            </span>
                          )}
                          {stage?.status === 'in_progress' && (
                            <span className="font-caption text-xs text-primary font-semibold">
                              In Progress
                            </span>
                          )}
                        </div>
                        <p className="font-body text-sm text-muted-foreground">{stage?.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Communication Tab */}
        {activeTab === 'communication' && (
          <div className="space-y-6">
            <h3 className="font-heading text-xl font-bold text-foreground mb-4">
              Communication Thread
            </h3>
            
            {/* Messages */}
            <div className="space-y-4 max-h-96 overflow-y-auto mb-6">
              {communications?.map((comm) => (
                <div
                  key={comm?.id}
                  className={`p-4 rounded-lg ${
                    comm?.isSystem
                      ? 'bg-muted/30 border border-border'
                      : comm?.role === 'renter' ?'bg-primary/5 border border-primary/20' :'bg-muted/50 border border-border'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon
                        name={comm?.isSystem ? 'Info' : comm?.role === 'renter' ? 'User' : 'UserCheck'}
                        size={16}
                        strokeWidth={2}
                        className="text-muted-foreground"
                      />
                      <span className="font-body font-semibold text-sm text-foreground">
                        {comm?.sender}
                      </span>
                    </div>
                    <span className="font-caption text-xs text-muted-foreground">
                      {new Date(comm?.timestamp)?.toLocaleString()}
                    </span>
                  </div>
                  <p className="font-body text-sm text-foreground whitespace-pre-line">{comm?.message}</p>
                </div>
              ))}
            </div>

            {/* Message Input */}
            <div className="border-t border-border pt-6">
              <label className="block font-body font-semibold text-foreground mb-3">
                Send Message to Mediator
              </label>
              <div className="flex space-x-3">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e?.target?.value)}
                  rows={3}
                  placeholder="Type your message here..."
                  className="flex-1 px-4 py-3 bg-background border border-border rounded-lg font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
                <Button
                  variant="primary"
                  onClick={handleSendMessage}
                  disabled={!newMessage?.trim()}
                  iconName="Send"
                  className="self-end"
                >
                  Send
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            <h3 className="font-heading text-xl font-bold text-foreground mb-4">
              Dispute Details
            </h3>
            
            <div className="space-y-4">
              <div>
                <p className="font-caption text-xs text-muted-foreground mb-2">Original Charge</p>
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-body text-sm text-muted-foreground">Amount</span>
                    <span className="font-body text-lg font-bold text-foreground">
                      ${charge?.chargeAmount?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-sm text-muted-foreground">Reason</span>
                    <span className="font-body text-sm text-foreground">{charge?.violationReason}</span>
                  </div>
                  {charge?.violationDescription && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="font-caption text-xs text-muted-foreground mb-1">Description</p>
                      <p className="font-body text-sm text-foreground">{charge?.violationDescription}</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <p className="font-caption text-xs text-muted-foreground mb-2">Your Dispute Claim</p>
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <p className="font-body text-sm text-foreground whitespace-pre-line">
                    {charge?.disputeReason || 'No dispute reason provided.'}
                  </p>
                </div>
              </div>

              {charge?.documentationUrls && charge?.documentationUrls?.length > 0 && (
                <div>
                  <p className="font-caption text-xs text-muted-foreground mb-2">Evidence Files</p>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="font-body text-sm text-foreground">
                      {charge?.documentationUrls?.length} file(s) uploaded
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResolutionTracker;